<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { onBeforeUnmount, ref } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';

defineProps({
  summary: { type: Object, default: () => ({}) },
  sections: { type: Array, default: () => [] },
  title: { type: String, default: 'Imóvel' },
  detailsTitle: { type: String, default: 'Informações do imóvel' },
});

const emit = defineEmits(['action', 'copied']);

const activeDialog = ref(null);
const copyFeedbackVisible = ref(false);
const closeLabel = 'Fechar';
const copyFeedbackLabel = 'Código copiado';
let copyFeedbackTimeout;

onBeforeUnmount(() => clearTimeout(copyFeedbackTimeout));

const closeDialog = () => {
  activeDialog.value = null;
};

const showCopyFeedback = () => {
  copyFeedbackVisible.value = true;
  clearTimeout(copyFeedbackTimeout);
  copyFeedbackTimeout = setTimeout(() => {
    copyFeedbackVisible.value = false;
  }, 1000);
};

const copyText = async action => {
  const { clipboardText } = action;
  if (!clipboardText) return;

  if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(clipboardText);
  }

  showCopyFeedback();
  emit('copied', clipboardText);
};

const openAction = async action => {
  if (!action || action.disabled) return;

  if (action.type === 'copy') {
    await copyText(action);
    return;
  }

  if (action.type === 'dialog') {
    activeDialog.value = action.dialog;
    emit('action', action.type, action);
    return;
  }

  if (action.href) {
    window.open(action.href, '_blank', 'noopener');
    emit('action', action.type || 'open', action);
    return;
  }

  if (action.emit) {
    emit('action', action.emit, action);
  }
};
</script>

<template>
  <div class="grid w-full min-w-0 gap-4">
    <LaisCard :title="title" prefix-icon="i-lucide-home">
      <div v-if="summary.tags?.length" class="mb-3 flex flex-wrap gap-2">
        <span
          v-for="tag in summary.tags"
          :key="tag"
          class="inline-flex items-center rounded-full border border-lais-primary/20 bg-lais-primary-50 px-2.5 py-1 text-[12px] font-semibold text-lais-primary"
        >
          {{ tag }}
        </span>
      </div>

      <div class="flex gap-3">
        <img
          v-if="summary.image"
          :src="summary.image"
          :alt="summary.title"
          class="h-20 w-24 shrink-0 rounded-lais-sm object-cover"
        />
        <div class="min-w-0">
          <h3 class="text-[15px] font-semibold leading-tight text-lais-text">
            {{ summary.title }}
          </h3>
          <p
            v-if="summary.description"
            class="mt-1 text-[13px] leading-relaxed text-lais-text-body"
          >
            {{ summary.description }}
          </p>
        </div>
      </div>

      <div
        v-if="summary.actions?.length"
        class="mt-4 grid gap-2 sm:grid-cols-2"
      >
        <button
          v-for="action in summary.actions"
          :key="action.key"
          type="button"
          :disabled="action.disabled"
          class="inline-flex min-h-10 items-center justify-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[13px] font-semibold text-lais-text transition hover:border-lais-primary hover:bg-lais-primary-50/30 focus-visible:outline-none focus-visible:shadow-lais-focus disabled:cursor-not-allowed disabled:opacity-50"
          @click="openAction(action)"
        >
          <span
            v-if="action.icon"
            class="size-3.5 shrink-0"
            :class="action.icon"
            aria-hidden="true"
          />
          <span>{{ action.label }}</span>
        </button>
      </div>
      <p
        v-if="copyFeedbackVisible"
        class="mt-2 text-[12px] text-lais-text-body"
        role="status"
        aria-live="polite"
      >
        {{ copyFeedbackLabel }}
      </p>
    </LaisCard>

    <LaisCard
      v-if="sections.length"
      :title="detailsTitle"
      prefix-icon="i-lucide-list-checks"
    >
      <div class="grid gap-4">
        <section
          v-for="section in sections"
          :key="section.key"
          class="grid gap-2 border-b border-lais-border-soft pb-4 last:border-b-0 last:pb-0"
        >
          <div class="flex gap-3">
            <span
              class="mt-0.5 size-4 shrink-0 text-lais-primary"
              :class="section.icon"
              aria-hidden="true"
            />
            <div class="min-w-0">
              <h3 class="text-[14px] font-semibold leading-snug text-lais-text">
                {{ section.title }}
              </h3>
              <p
                v-if="section.subtitle"
                class="mt-1 text-[13px] leading-relaxed text-lais-text-body"
              >
                {{ section.subtitle }}
              </p>
            </div>
          </div>

          <div v-if="section.actions?.length" class="ml-7 flex flex-wrap gap-2">
            <button
              v-for="action in section.actions"
              :key="`${section.key}-${action.key}`"
              type="button"
              :disabled="action.disabled"
              class="inline-flex min-h-8 items-center justify-center gap-2 rounded-lais-sm px-0 py-1 text-[13px] font-semibold text-lais-primary transition hover:text-lais-primary-hover disabled:cursor-not-allowed disabled:text-lais-text-muted"
              @click="openAction(action)"
            >
              <span>{{ action.label }}</span>
              <span
                v-if="action.icon"
                class="size-3.5 shrink-0"
                :class="action.icon"
                aria-hidden="true"
              />
            </button>
          </div>
        </section>
      </div>
    </LaisCard>

    <teleport to="body">
      <div
        v-if="activeDialog"
        class="fixed inset-0 z-[1000] grid place-items-center bg-black/40 p-4"
        role="dialog"
        aria-modal="true"
        :aria-label="activeDialog.title"
      >
        <section
          class="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lais bg-white p-5 shadow-lais-pop"
        >
          <div class="flex items-start justify-between gap-3">
            <h2 class="text-[18px] font-semibold leading-tight text-lais-text">
              {{ activeDialog.title }}
            </h2>
            <button
              type="button"
              class="grid size-8 shrink-0 place-items-center rounded-full text-lais-text-muted transition hover:bg-lais-bg-soft hover:text-lais-text"
              :aria-label="closeLabel"
              @click="closeDialog"
            >
              <span class="i-lucide-x size-4" aria-hidden="true" />
            </button>
          </div>

          <div class="mt-4 grid gap-3">
            <p
              v-for="paragraph in activeDialog.paragraphs"
              :key="paragraph"
              class="text-[14px] leading-relaxed text-lais-text-body"
            >
              {{ paragraph }}
            </p>
          </div>

          <div class="mt-5 flex justify-end">
            <button
              type="button"
              class="inline-flex min-h-10 items-center justify-center rounded-lais bg-lais-primary px-4 py-2 text-[14px] font-semibold text-white transition hover:bg-lais-primary-hover"
              @click="closeDialog"
            >
              {{ activeDialog.confirmLabel || 'Ok, entendi' }}
            </button>
          </div>
        </section>
      </div>
    </teleport>
  </div>
</template>
